import React, { useEffect, useState } from 'react';
import {
    Modal,
    Box,
    TextField,
    Button,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Grid,
    Typography,
    CircularProgress,
    FormHelperText,
} from '@mui/material';
import { Communication } from "../../../data/Communication";
import { useGlobal } from "../../../utils/GlobalContext";
import useCommunicationTypes from "../../communicationTypes/hooks/useCommunicationTypes";
import { EventsListItem } from "../../../data/eventInfo";
import { getEventInfo } from "../../../services/EventInformation";
import Editor from "../../../utils/RTEditor";
import IconList from "../../../data/account/socialMediaIcons";
import 'react-quill/dist/quill.snow.css';
import dayjs, { Dayjs } from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';

interface Props {
    isOpen: boolean;
    onClose: () => void;
    onAdd: (
        communicationId: string,
        title: string,
        comTypeId: string,
        eventName: string,
        status: string,
        composedBy: string,
        composedTimeStamp: string,
        sendDateAsString: string,
        message: string
    ) => Promise<void>;
    existingCommunication: Communication[];
}

const AddModal: React.FC<Props> = ({ isOpen, onClose, onAdd, existingCommunication }) => {
    const [communicationId, setCommunicationId] = useState('');
    const [title, setTitle] = useState('');
    const [comTypeId, setComTypeId] = useState('');
    const [status, setStatus] = useState('Draft');
    const [composedBy, setComposedBy] = useState('');
    const [message, setMessage] = useState('');
    const [events, setEvents] = useState<EventsListItem[]>([]);
    const [eventName, setEventName] = useState('');
    const [composedTimeStamp, setComposedTimeStamp] = useState('');
    const [sendDate, setSendDate] = useState<Dayjs | null>(dayjs());
    const [sendDateAsString, setSendDateAsString] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});

    const { state } = useGlobal();
    const { communicationTypes } = useCommunicationTypes();

    useEffect(() => {
        if (isOpen) {
            const now = dayjs();
            const composed = new Date().toISOString();
            const fullName = `${state.UserProfile.FirstName} ${state.UserProfile.LastName}`;

            setCommunicationId(crypto.randomUUID());
            setComposedBy(fullName);
            setComposedTimeStamp(composed);
            setSendDate(now);
            setSendDateAsString(now.toISOString());

            fetchEvents();
        }
    }, [isOpen]);

    const fetchEvents = async () => {
        try {
            const eventList = await getEventInfo();
            setEvents(eventList);
        } catch (error) {
            console.error("Error fetching events:", error);
        }
    };

    const validate = () => {
        const newErrors: { [key: string]: string } = {};
        if (!title.trim()) newErrors.title = "Title is required";
        if (!comTypeId.trim()) newErrors.comTypeId = "Communication Type is required";
        if (!eventName.trim()) newErrors.eventName = "Event is required";
        if (!status.trim()) newErrors.status = "Status is required";
        if (!message.trim()) newErrors.message = "Message cannot be empty";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleAdd = async () => {
        if (!validate()) return;
        setIsSaving(true);
        try {
            await onAdd(
                communicationId,
                title,
                comTypeId,
                eventName,
                status,
                composedBy,
                composedTimeStamp,
                sendDateAsString,
                message
            );
            handleReset();
            onClose();
        } catch (error) {
            console.error('Error saving communication:', error);
            alert('Failed to save. Please try again.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleDateChange = (newValue: Dayjs | null) => {
        setSendDate(newValue);
        if (newValue) {
            setSendDateAsString(newValue.toISOString());
        } else {
            setSendDateAsString('');
        }
    };

    const handleReset = () => {
        setTitle('');
        setComTypeId('');
        setStatus('Draft');
        setEventName('');
        setMessage('');
        setSendDate(dayjs());
        setSendDateAsString(dayjs().toISOString());
        setErrors({});
    };

    return (
        <Modal open={isOpen} onClose={onClose}>
            <Box sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '95vw',
                height: '95vh',
                bgcolor: 'background.paper',
                boxShadow: 24,
                p: 3,
                overflow: 'auto',
                display: 'flex',
                flexDirection: 'column',
            }}>
                <TextField
                    fullWidth
                    label="Title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    margin="normal"
                    error={!!errors.title}
                    helperText={errors.title}
                />

                <Box display="flex" gap={2} sx={{ width: '100%' }}>
                    <FormControl fullWidth margin="normal" error={!!errors.eventName}>
                        <InputLabel>Event ID</InputLabel>
                        <Select
                            value={eventName}
                            onChange={(e) => setEventName(e.target.value)}
                            label="Event ID"
                        >
                            {events.map((event) => (
                                <MenuItem key={event.Name} value={event.Name}>
                                    {event.Name}
                                </MenuItem>
                            ))}
                        </Select>
                        {!!errors.eventName && <FormHelperText>{errors.eventName}</FormHelperText>}
                    </FormControl>

                    <FormControl fullWidth margin="normal" error={!!errors.comTypeId}>
                        <InputLabel>Communication Type</InputLabel>
                        <Select
                            value={comTypeId}
                            onChange={(e) => setComTypeId(e.target.value)}
                            label="Communication Type"
                        >
                            {communicationTypes.map((type) => {
                                const IconComponent = IconList[type.icon as keyof typeof IconList];
                                return (
                                    <MenuItem key={type.comTypeId} value={type.comTypeId}>
                                        <Box display="flex" alignItems="center">
                                            {IconComponent && <IconComponent style={{ marginRight: 8 }} />}
                                            {type.comType}
                                        </Box>
                                    </MenuItem>
                                );
                            })}
                        </Select>
                        {!!errors.comTypeId && <FormHelperText>{errors.comTypeId}</FormHelperText>}
                    </FormControl>
                </Box>

                <Box display="flex" gap={2} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                    <Box flex={1} minWidth={200}>
                        <FormControl fullWidth margin="normal" error={!!errors.status}>
                            <InputLabel>Status</InputLabel>
                            <Select
                                value={status}
                                onChange={(e) => setStatus(e.target.value)}
                                label="Status"
                            >
                                <MenuItem value="Draft">Draft</MenuItem>
                                <MenuItem value="Ready">Ready</MenuItem>
                                <MenuItem value="Sent">Sent</MenuItem>
                                <MenuItem value="Reject">Reject</MenuItem>
                            </Select>
                            {!!errors.status && <FormHelperText>{errors.status}</FormHelperText>}
                        </FormControl>
                    </Box>

                    <Box flex={1} minWidth={200}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DemoContainer components={['DateTimePicker']}>
                                <DateTimePicker
                                    label="Send Date"
                                    value={sendDate}
                                    onChange={handleDateChange}
                                    sx={{ width: '90%', minWidth: '200px' }}
                                />
                            </DemoContainer>
                        </LocalizationProvider>
                    </Box>
                </Box>

                <Grid item xs={12}>
                    <Typography variant="h6" textAlign="center">Message</Typography>
                    <div className="EditorContainer">
                        <Editor
                            editorName="editor"
                            content={message}
                            saveContent={setMessage}
                            showSaveButton={false}
                        />
                        {!!errors.message && (
                            <Typography color="error" fontSize="0.75rem" mt={1}>
                                {errors.message}
                            </Typography>
                        )}
                    </div>
                </Grid>

                <Button
                    variant="contained"
                    onClick={handleAdd}
                    disabled={isSaving}
                    sx={{ marginTop: 2 }}
                >
                    {isSaving ? <CircularProgress size={24} color="inherit" /> : 'Add'}
                </Button>
            </Box>
        </Modal>
    );
};

export default AddModal;
